import React, { useEffect } from "react";
import { X, ExternalLink } from "lucide-react";

import {
  SENTIMENT_LABELS,
  sentimentBadgeClasses,
  CONSTRAINT_LABELS,
  SITING_LABELS,
  yesNo,
  dualSignalLabel,
} from "../lib/format";

export default function WorkshopDrawer({ workshop, open, onClose }) {
  useEffect(() => {
    function handler(e) {
      if (e.key === "Escape") onClose?.();
    }
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || !workshop) return null;
  const w = workshop;
  const interp = w.sentiment_interpretive || w.sentiment;
  const explicit = w.sentiment_explicit;
  const diverged = explicit && interp && explicit !== interp;

  return (
    <>
      <div
        className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[1200]"
        onClick={onClose}
        data-testid="drawer-overlay"
      />
      <aside
        className="fixed inset-y-0 right-0 w-full max-w-2xl bg-white border-l border-gray-200 shadow-2xl z-[1201] overflow-y-auto"
        data-testid="workshop-drawer"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-start justify-between">
          <div>
            <div className="flex items-center flex-wrap gap-2">
              <span
                className={
                  "inline-block px-2 py-0.5 text-[11px] font-medium " +
                  sentimentBadgeClasses(interp)
                }
                data-testid="drawer-sentiment-interp"
              >
                {SENTIMENT_LABELS[interp] || "Not classified"}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-gray-500">
                interpretive
              </span>
              {diverged ? (
                <>
                  <span className="text-gray-300">·</span>
                  <span
                    className={
                      "inline-block px-2 py-0.5 text-[11px] font-medium " +
                      sentimentBadgeClasses(explicit)
                    }
                    data-testid="drawer-sentiment-explicit"
                  >
                    {SENTIMENT_LABELS[explicit] || "—"}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-gray-500">
                    explicit
                  </span>
                </>
              ) : null}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mt-1.5">
              {w.workshop_name}
            </h2>
            <p className="text-sm text-gray-600 mt-0.5">
              {w.parish_or_town}
              {w.county ? ` · ${w.county}` : ""}
              {w.landscape_context ? ` · ${w.landscape_context}` : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 border border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50"
            data-testid="drawer-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {w.sentiment_reasoning ? (
            <div
              className="border-l-4 border-[#2F4F4F] bg-[#F8FAF9] p-3 text-sm text-gray-800"
              data-testid="drawer-sentiment-reasoning"
            >
              <span className="text-[11px] uppercase tracking-wider text-[#2F4F4F] font-semibold">
                Classification reasoning
              </span>
              <p className="mt-1">{w.sentiment_reasoning}</p>
            </div>
          ) : null}

          <SubHeader title="Wind discussion summary" testid="drawer-summary" />
          <p className="text-sm text-gray-800 leading-relaxed">
            {w.wind_discussion_summary || "—"}
          </p>

          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <KV label="Date" value={w.date || "—"} />
            <KV label="Attendance" value={w.attendance ?? "—"} mono />
            <KV
              label="Acceptability score"
              value={w.acceptability_score_text || w.acceptability_score || "—"}
              mono
            />
            <KV
              label="Acceptability band"
              value={w.acceptability_band || "—"}
              capitalize
            />
            <KV
              label="Turbines proposed"
              value={w.turbine_count_proposed || "—"}
              mono
            />
            <KV
              label="Preferred size"
              value={w.preferred_size || "—"}
              capitalize
            />
            <KV
              label="Prefers fewer, larger"
              value={dualSignalLabel(
                w.prefers_fewer_larger_explicit,
                w.prefers_fewer_larger_implicit
              )}
            />
            <KV
              label="Prefers clustering"
              value={dualSignalLabel(
                w.prefers_clustering_explicit,
                w.prefers_clustering_implicit
              )}
            />
            <KV
              label="SDNP / planning cited"
              value={yesNo(w.sdnp_or_planning_constraint_mentioned)}
            />
            <KV
              label="Constrained by context (not opposed)"
              value={yesNo(w.constrained_by_context_not_opposed)}
            />
          </div>

          {w.prefers_fewer_larger_reasoning ||
          w.prefers_clustering_reasoning ? (
            <div
              className="border border-gray-200 bg-gray-50 p-3 space-y-2 text-xs text-gray-700"
              data-testid="drawer-preference-reasoning"
            >
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
                Behavioural reasoning
              </div>
              {w.prefers_fewer_larger_reasoning ? (
                <p>
                  <span className="font-medium text-gray-900">
                    Fewer / larger:
                  </span>{" "}
                  {w.prefers_fewer_larger_reasoning}
                </p>
              ) : null}
              {w.prefers_clustering_reasoning ? (
                <p>
                  <span className="font-medium text-gray-900">Clustering:</span>{" "}
                  {w.prefers_clustering_reasoning}
                </p>
              ) : null}
            </div>
          ) : null}

          <SubHeader title="Behavioural evidence of engagement" />
          <Chips
            items={w.behavioural_evidence_of_support || []}
            tone="slate"
            testid="drawer-behavioural"
          />

          <SubHeader title="Siting logic" />
          <Chips
            items={(w.siting_logic || []).map((k) => SITING_LABELS[k] || k)}
            testid="drawer-siting"
          />

          <SubHeader title="Constraints raised" />
          <Chips
            items={(w.constraints || []).map(
              (k) => CONSTRAINT_LABELS[k] || k
            )}
            tone="rose"
            testid="drawer-constraints"
          />
          {w.other_constraints_text ? (
            <p className="text-xs text-gray-500 -mt-3">
              Other: {w.other_constraints_text}
            </p>
          ) : null}

          <SubHeader title="Conditions for support" />
          <Chips
            items={w.conditions_for_support || []}
            tone="emerald"
            testid="drawer-conditions"
          />

          <SubHeader title="Language & quotes" />
          <div className="space-y-3">
            {(w.positive_phrases || []).map((p, i) => (
              <blockquote
                key={`pos-${i}`}
                className="border-l-2 border-emerald-500 pl-3 text-sm text-gray-800 italic"
                data-testid={`drawer-quote-positive-${i}`}
              >
                “{p}”
              </blockquote>
            ))}
            {(w.cautionary_phrases || []).map((p, i) => (
              <blockquote
                key={`cau-${i}`}
                className="border-l-2 border-rose-500 pl-3 text-sm text-gray-800 italic"
                data-testid={`drawer-quote-cautionary-${i}`}
              >
                “{p}”
              </blockquote>
            ))}
            {(w.positive_phrases || []).length === 0 &&
            (w.cautionary_phrases || []).length === 0 ? (
              <p className="text-sm text-gray-500">
                No direct quotes extracted.
              </p>
            ) : null}
          </div>

          {w.source_url ? (
            <a
              href={w.source_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-sm text-white bg-[#2F4F4F] hover:bg-[#1A3636] px-4 py-2.5 transition-colors"
              data-testid="drawer-pdf-link"
            >
              Open full workshop PDF <ExternalLink className="h-4 w-4" />
            </a>
          ) : null}

          <p className="text-[11px] text-gray-400 pt-4 border-t border-gray-100">
            Fields extracted by Claude Sonnet 4.5 using interpretive coding
            (explicit statements + behavioural signals). Raw extraction preserved in
            the backend dataset.
          </p>
        </div>
      </aside>
    </>
  );
}

function SubHeader({ title, testid }) {
  return (
    <h3
      className="text-[11px] font-semibold uppercase tracking-wider text-gray-500"
      data-testid={testid}
    >
      {title}
    </h3>
  );
}

function KV({ label, value, mono, capitalize }) {
  return (
    <div className="flex flex-col">
      <span className="text-[11px] text-gray-500 uppercase tracking-wider">
        {label}
      </span>
      <span
        className={
          "text-gray-900 " +
          (mono ? "font-mono " : "") +
          (capitalize ? "capitalize " : "")
        }
      >
        {value}
      </span>
    </div>
  );
}

function Chips({ items, tone, testid }) {
  if (!items || items.length === 0)
    return (
      <p className="text-sm text-gray-500" data-testid={testid}>
        —
      </p>
    );
  const cls =
    tone === "rose"
      ? "bg-rose-50 text-rose-800 border-rose-200"
      : tone === "emerald"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : tone === "slate"
      ? "bg-slate-50 text-slate-800 border-slate-200"
      : "bg-gray-50 text-gray-800 border-gray-200";
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {items.map((i, idx) => (
        <span
          key={idx}
          className={"inline-block px-2 py-0.5 text-[12px] border " + cls}
        >
          {i}
        </span>
      ))}
    </div>
  );
}
